import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Game } from '@/types/game';
import GameCard from '@/components/game/GameCard';
import { SEO } from '@/components/SEO';
import { Skeleton } from '@/components/ui/skeleton';

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q');

  const { data: games, isLoading, error } = useQuery({
    queryKey: ['search-page-games', query],
    queryFn: async (): Promise<Game[]> => {
      if (!query) return [];

      const { data, error } = await supabase
        .from('games')
        .select('*')
        .ilike('title', `%${query}%`)
        .eq('hidden', false)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      return (data || []).map((dbGame: any): Game => ({
        id: dbGame.id,
        title: dbGame.title,
        description: dbGame.description || '',
        thumbnail: dbGame.thumbnail || '',
        category: dbGame.category,
        tags: dbGame.tags || [],
        featured: dbGame.featured || false,
        trending: dbGame.trending || false,
        isNew: dbGame.is_new || false,
        playCount: dbGame.play_count || 0,
        fakePlayCount: dbGame.fake_play_count,
        rating: Number(dbGame.rating) || 0,
        createdAt: dbGame.created_at,
        updatedAt: dbGame.updated_at,
        embedType: 'iframe',
        gameUrl: dbGame.game_url || '',
      }));
    },
    enabled: !!query,
  });

  return (
    <>
      <SEO 
        pageType="website" 
        title={`Search results for "${query}"`} 
        description={`Find games matching your search for "${query}" on GameHub.`} 
      />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 w-full">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Search Results</h1>
          <p className="text-gray-600 text-lg mb-8">
            {isLoading ? 'Searching for games...' : `Found ${games?.length || 0} games for "${query}"`}
          </p>

          {isLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
              {Array.from({ length: 12 }).map((_, index) => (
                <Skeleton key={index} className="w-full aspect-[4/5] rounded-xl" />
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12 text-red-600">
              <p>Error fetching search results. Please try again later.</p>
            </div>
          ) : games && games.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
              {games.map(game => (
                <GameCard key={game.id} game={game} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No games found for your search.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SearchPage;
